/**
 * weatherTheme.js
 *
 * Maps the current forecast → a "weather state" (clear / rain / cloudy / night)
 * and returns a palette + scene config used across the redesigned pages.
 *
 * Source of truth for the pastel-first, weather-responsive look. Keeps the
 * technical data out of the view layer — components just call `getTheme(forecast)`.
 */

const STATES = {
  clear: {
    id: "clear",
    label: "Clear",
    // Soft warm sky — sky-100 → blue-50 → amber-50
    gradient:
      "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 40%, #fef3c7 100%)",
    textPrimary: "#0f172a", // slate-900
    textSecondary: "#475569", // slate-600
    textMuted: "#64748b", // slate-500
    accent: "#f59e0b", // amber-500
    accentSoft: "rgba(245, 158, 11, 0.14)",
    surface: "rgba(255, 255, 255, 0.55)",
    surfaceBorder: "rgba(255, 255, 255, 0.70)",
    chip: "rgba(255, 255, 255, 0.60)",
    statusBar: "#e0f2fe",
    sceneBg: "radial-gradient(circle at 70% 25%, rgba(251,191,36,0.35), transparent 55%)",
    illustration: "sun", // which SVG scene to render
  },
  cloudy: {
    id: "cloudy",
    label: "Cloudy",
    gradient: "linear-gradient(135deg, #f4f4f5 0%, #f1f5f9 45%, #e5e7eb 100%)",
    textPrimary: "#1e293b",
    textSecondary: "#475569",
    textMuted: "#64748b",
    accent: "#64748b", // slate-500
    accentSoft: "rgba(100, 116, 139, 0.14)",
    surface: "rgba(255, 255, 255, 0.55)",
    surfaceBorder: "rgba(255, 255, 255, 0.70)",
    chip: "rgba(255, 255, 255, 0.60)",
    statusBar: "#f1f5f9",
    sceneBg: "radial-gradient(circle at 50% 30%, rgba(148,163,184,0.35), transparent 60%)",
    illustration: "clouds",
  },
  rain: {
    id: "rain",
    label: "Rain",
    gradient: "linear-gradient(135deg, #e2e8f0 0%, #e0e7ff 45%, #dbeafe 100%)",
    textPrimary: "#1e293b",
    textSecondary: "#334155",
    textMuted: "#64748b",
    accent: "#4f46e5", // indigo-600
    accentSoft: "rgba(79, 70, 229, 0.14)",
    surface: "rgba(255, 255, 255, 0.55)",
    surfaceBorder: "rgba(255, 255, 255, 0.70)",
    chip: "rgba(255, 255, 255, 0.65)",
    statusBar: "#e0e7ff",
    sceneBg: "radial-gradient(circle at 50% 30%, rgba(99,102,241,0.35), transparent 60%)",
    illustration: "rain",
  },
  storm: {
    id: "storm",
    label: "Thunder",
    gradient: "linear-gradient(135deg, #1e293b 0%, #312e81 50%, #0f172a 100%)",
    textPrimary: "#f1f5f9",
    textSecondary: "#cbd5e1",
    textMuted: "#94a3b8",
    accent: "#fbbf24",
    accentSoft: "rgba(251, 191, 36, 0.20)",
    surface: "rgba(15, 23, 42, 0.55)",
    surfaceBorder: "rgba(255, 255, 255, 0.15)",
    chip: "rgba(15, 23, 42, 0.55)",
    statusBar: "#1e293b",
    sceneBg: "radial-gradient(circle at 70% 30%, rgba(251,191,36,0.25), transparent 60%)",
    illustration: "storm",
  },
  night: {
    id: "night",
    label: "Night",
    gradient: "linear-gradient(135deg, #1e1b4b 0%, #312e81 45%, #1e293b 100%)",
    textPrimary: "#f8fafc",
    textSecondary: "#cbd5e1",
    textMuted: "#94a3b8",
    accent: "#a5b4fc", // indigo-300
    accentSoft: "rgba(165, 180, 252, 0.18)",
    surface: "rgba(30, 27, 75, 0.45)",
    surfaceBorder: "rgba(255, 255, 255, 0.12)",
    chip: "rgba(30, 27, 75, 0.55)",
    statusBar: "#1e1b4b",
    sceneBg: "radial-gradient(circle at 20% 80%, rgba(165,180,252,0.25), transparent 60%)",
    illustration: "night",
  },
};

function classifyWeatherCode(code) {
  if (code == null) return null;
  if (code === 0) return "clear";
  if (code <= 2) return "clear";
  if (code === 3) return "cloudy";
  if (code >= 45 && code <= 48) return "cloudy";
  if (code >= 51 && code <= 67) return "rain";
  if (code >= 71 && code <= 77) return "cloudy"; // snow uses cloudy palette
  if (code >= 80 && code <= 82) return "rain";
  if (code >= 95) return "storm";
  return "cloudy";
}

/**
 * Derive weather state from a forecast payload.
 * Prioritises: active/imminent storm > upcoming rain > is_day flag > weather_code mapping.
 *
 * NOTE: High CAPE alone is NOT enough to trigger the dark "storm" theme — convective
 * potential of 2000+ J/kg is perfectly normal on hot Indian afternoons (pre-monsoon
 * Tirunelveli, Chennai, Hyderabad regularly hit 2000-3000 J/kg at midday with zero
 * precipitation). We only switch to the dark theme when a storm is ACTUALLY
 * happening now OR is imminent (rain building within the next 2h alongside CAPE).
 */
export function getWeatherState(forecast) {
  if (!forecast) return "clear";
  const cur = forecast.current || {};
  const isDay = cur.is_day !== 0;

  // Upcoming precipitation in next 2h (8 minutely_15 buckets × 15min = 2h)
  const upcoming2h = (forecast.minutely_15?.precipitation || []).slice(0, 8);
  const peakSoon = upcoming2h.length ? Math.max(...upcoming2h) : 0;

  // First-hour CAPE — convective potential
  const capeNow = (forecast.hourly?.cape || [])[0] || 0;

  const baseState = classifyWeatherCode(cur.weather_code) || "clear";

  // Storm theme: only when a storm is genuinely active/imminent.
  //   ✓ Reported thunderstorm weather code (95-99)
  //   ✓ High CAPE *with* imminent precip (≥ 0.5 mm in next 2h)
  //   ✗ NOT just high CAPE alone — that's every hot Indian afternoon
  const activeOrImminentStorm =
    baseState === "storm" || (capeNow >= 2000 && peakSoon >= 0.5);
  if (activeOrImminentStorm) return "storm";

  if (peakSoon >= 0.5 || baseState === "rain") return "rain";
  if (!isDay) return "night";
  return baseState;
}

export function getTheme(_forecast) {
  // Theme is INTENTIONALLY locked to the warm "clear" palette regardless of
  // weather state. Earlier versions switched to dark purple/storm/night themes
  // by location, which felt jarring across users in different cities. The
  // weather state is still surfaced in two informative ways:
  //   1. The verdict-driven scene illustration (sun / clouds / rain / storm)
  //      inside the Rain Confidence ring — see VerdictPage `sceneTheme`.
  //   2. The 6-hour timeline weather icons.
  // Keeping the background consistent makes the app feel calm and predictable.
  return STATES.clear;
}

export function getThemeById(id) {
  return STATES[id] || STATES.clear;
}

// Convenience: all state ids for preview/testing
export const WEATHER_STATES = Object.keys(STATES);
